import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import { Plus, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';

function ExamsPage() {
  const { getAuthHeader } = useAuth();
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ subject_id: '', title: '', date: '', topics: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [exRes, subRes] = await Promise.all([
        api.getExams(headers),
        api.getSubjects(headers)
      ]);
      setExams(exRes.data);
      setSubjects(subRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Laden');
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.subject_id || !formData.date) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    try {
      const headers = getAuthHeader();
      const data = {
        subject_id: formData.subject_id,
        title: formData.title,
        date: new Date(formData.date).toISOString(),
        topics: formData.topics.split(',').map(t => t.trim()).filter(t => t)
      };
      await api.createExam(data, headers);
      toast.success('Klausur hinzugefügt!');
      setShowDialog(false);
      setFormData({ subject_id: '', title: '', date: '', topics: '' });
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler');
    }
  };

  const updatePrep = async (id, status) => {
    try {
      const headers = getAuthHeader();
      await api.updateExam(id, status, headers);
      setExams(prev => prev.map(ex => ex.id === id ? { ...ex, preparation_status: status } : ex));
      toast.success('Aktualisiert');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler');
    }
  };

  const now = new Date();
  const upcoming = exams.filter(ex => new Date(ex.date) >= now);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold text-slate-900 mb-2">Klausuren</h1>
          <p className="text-lg text-slate-600">{upcoming.length} anstehend</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold px-6">
              <Plus size={20} className="mr-2" />
              Klausur hinzufügen
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">Neue Klausur</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fach</label>
                <Select value={formData.subject_id} onValueChange={(value) => setFormData({ ...formData, subject_id: value })}>
                  <SelectTrigger className="h-12 rounded-2xl">
                    <SelectValue placeholder="Fach wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.icon} {s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Titel</label>
                <Input
                  placeholder="z.B. Klausur Kapitel 3-5"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="h-12 rounded-2xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Datum</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="h-12 rounded-2xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Themen (Komma getrennt)</label>
                <Input
                  placeholder="z.B. Lineare Funktionen, Gleichungen"
                  value={formData.topics}
                  onChange={(e) => setFormData({ ...formData, topics: e.target.value })}
                  className="h-12 rounded-2xl"
                />
              </div>
              <Button onClick={handleCreate} className="w-full h-12 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold">
                Hinzufügen
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {upcoming.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
          <p className="text-slate-500">Keine anstehenden Klausuren</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {upcoming.map(exam => {
            const subject = subjects.find(s => s.id === exam.subject_id);
            const examDate = new Date(exam.date);
            const daysUntil = Math.ceil((examDate - now) / (1000 * 60 * 60 * 24));
            const isUrgent = daysUntil <= 7;
            
            return (
              <div key={exam.id} className={`bg-white rounded-3xl p-6 border-2 ${isUrgent ? 'border-red-200' : 'border-slate-100'}`}>
                <div className="flex items-start gap-4 mb-4">
                  {subject && (
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl" style={{ backgroundColor: subject.color + '20' }}>
                      {subject.icon}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-600 mb-1">{subject?.name || 'Unbekannt'}</p>
                    <h3 className="text-xl font-heading font-bold text-slate-900">{exam.title}</h3>
                    <div className="flex items-center gap-2 mt-2">
                      <Calendar size={14} className={isUrgent ? 'text-red-600' : 'text-slate-500'} />
                      <span className={`text-sm font-medium ${isUrgent ? 'text-red-600' : 'text-slate-600'}`}>
                        {examDate.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                    <div className={`inline-flex items-center gap-1 mt-2 px-3 py-1 rounded-full text-xs font-bold ${isUrgent ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                      In {daysUntil} {daysUntil === 1 ? 'Tag' : 'Tagen'}
                    </div>
                  </div>
                </div>

                {exam.topics && exam.topics.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-medium text-slate-600 mb-2">Themen:</p>
                    <div className="flex flex-wrap gap-2">
                      {exam.topics.map((topic, i) => (
                        <span key={i} className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full">{topic}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-slate-600">Vorbereitung</p>
                    <p className="text-xs font-bold text-primary-600">{exam.preparation_status}%</p>
                  </div>
                  <Progress value={exam.preparation_status} className="h-2 mb-3" />
                  <div className="flex gap-2">
                    {[0, 25, 50, 75, 100].map(val => (
                      <button
                        key={val}
                        onClick={() => updatePrep(exam.id, val)}
                        className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all duration-200 ${
                          exam.preparation_status === val
                            ? 'bg-primary-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {val}%
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ExamsPage;
